import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import lowerCase from 'lodash/lowerCase';
import partial from 'lodash/partial';
import { batch } from 'react-redux';

import {
  LOGIN_FAILURE,
  LOGIN_REQUEST,
  LOGIN_SUCCESS,
  LOGOUT,
  PASSWORD_CHANGE_FAILURE,
  PASSWORD_CHANGE_REQUEST,
  PASSWORD_CHANGE_SUCCESS,
  REGISTRATION_FAILURE,
  REGISTRATION_REQUEST,
  REGISTRATION_SUCCESS,
  RESET_ACCESSES,
  SET_ARCHIVE_WARNING_SHOWN,
  SET_AUTO_EXPIRED_TOKEN,
  SET_LOCALES,
  SET_LOGIN_METHOD,
  SET_REQUIRE_PASSWORD_CHANGE,
  SET_SIGN_OFF_DIALOG_VISIBILITY,
  SET_SSO_LOGIN_FAILURE,
  SET_TIMEZONES,
  SET_TOKEN_STATUS,
  SHOW_IDP_VIEW,
  UPDATE_LOGON_USER,
  UPDATE_TOKEN,
} from './constants';
import { expireToken } from './ExpireToken';
import { fetchLocales, fetchTimezones } from '../../api/external-api';
import smartSolveApi, { syncAPICall } from '../../api/instances/smartsolve';
import { getUICacheObjects } from '../../api/ui-cache-objects';
import LOGIN_METHODS from '../../constants/loginMethodsConstants';
import * as Urls from '../../constants/Urls';
import { WARNING_SOURCES } from '../../constants/WarningsConstants';
import { selectSessionExpirationErrorCode } from '../../selectors/AuthSelectors';
import { setUserCacheKey } from '../../utils/cacheUtils';
import { closePrevChildrenWindows } from '../../utils/childrenWindowsUtils';
import { registerPasskeyCredentialsIfNeeded } from '../../utils/credential.utils';
import { getEnvVar } from '../../utils/env';
import { hasValue, isSet, parseBoolean, parseNilOrValue } from '../../utils/fns';
import { fabricSSOUrl, getQsParam } from '../../utils/qs';
import { getStorageItem, removeStorageItem } from '../../utils/storageUtils';
import { handleGetUICacheObjectsResponse } from '../../utils/uiCacheObjects.utils';
import { checkPasskeyCredentialsIfNeeded } from '../CredentialActions';
import { addError, clearErrors, handleErrors } from '../ErrorActions';
import { addSuccess } from '../SuccessActions';
import { clearWarnings } from '../WarningActions';

const getTokenStatus = (keyName) => {
  const result = parseBoolean(getStorageItem(keyName));
  removeStorageItem();

  return result;
};

const filterNonExpiredErrors = (sessionExpirationErrorCode) => ({ code }) =>
  !(code === sessionExpirationErrorCode || code === '50001');

const parseResponse = (authErrors) => {
  try {
    return JSON.parse(authErrors);
  } catch {
    return { hasError: true, errorCode: '', errorMessage: '' };
  }
};

const getSSOUrl = (data, urlType, urlPath) => {
  const url = get(data, urlType, '');

  return fabricSSOUrl(url, urlPath);
};

const setRequirePasswordChange = (isRequired, userLogonCode) => ({
  type: SET_REQUIRE_PASSWORD_CHANGE,
  isRequired,
  userLogonCode,
});

export const setArchiveWarningShown = () => ({
  type: SET_ARCHIVE_WARNING_SHOWN,
});

export const updateLogonUser = (isLogonUserChanged) => ({
  type: UPDATE_LOGON_USER,
  isLogonUserChanged,
});

export const loginSuccess = (loginDetails, password) => ({
  type: LOGIN_SUCCESS,
  authData: loginDetails,
  password,
});

const passwordChangeSuccess = () => ({
  type: PASSWORD_CHANGE_SUCCESS,
});

const loginFailure = (loginError) => ({
  type: LOGIN_FAILURE,
  loginError,
});

const passwordChangeFailure = (passwordChangeError) => ({
  type: PASSWORD_CHANGE_FAILURE,
  passwordChangeError,
});

const logout = () => ({
  type: LOGOUT,
});

export const setSignOffDialogVisibility = (showSignOffDialog) => ({
  type: SET_SIGN_OFF_DIALOG_VISIBILITY,
  showSignOffDialog,
});

export const setSSOLoginFailure = (isSSOLoginFailure) => ({
  type: SET_SSO_LOGIN_FAILURE,
  isSSOLoginFailure,
});

export const showIdpView = ({ isSignOffStep = false }) => ({
  type: SHOW_IDP_VIEW,
  isSignOffStep,
});

export const setTokenStatus = ({
  isTokenUpdated,
  showSignOffDialog,
  showFEDSSOPopup,
  isSignOffStep,
}) => ({
  type: SET_TOKEN_STATUS,
  isTokenUpdated,
  ...(isSet(showSignOffDialog) && { showSignOffDialog }),
  ...(isSet(showFEDSSOPopup) && { showFEDSSOPopup }),
  ...(isSet(isSignOffStep) && { isSignOffStep }),
});

export const updateToken = (data) => ({
  type: UPDATE_TOKEN,
  data,
});

export const setAutoExpiredToken = () => ({
  type: SET_AUTO_EXPIRED_TOKEN,
});

const registrationSuccess = (data) => ({
  type: REGISTRATION_SUCCESS,
  data,
});

const registrationFailure = (error) => ({
  type: REGISTRATION_FAILURE,
  error,
});

const setLocales = (data) => ({
  type: SET_LOCALES,
  data,
});

const setTimezones = (data) => ({
  type: SET_TIMEZONES,
  data,
});

export const discardAccesses = () => (dispatch) => {
  dispatch({ type: RESET_ACCESSES });
  localStorage.removeItem('token');
};

export const syncTokenStatus = () => (dispatch) => {
  const isTokenUpdated = getTokenStatus('isTokenUpdated');

  dispatch(setTokenStatus({ isTokenUpdated }));
};

export const failSSOLogin = () => (dispatch) => {
  const loginFailureResponse = localStorage.getItem('loginFailureResponse');

  if (isNil(loginFailureResponse)) {
    return null;
  }

  localStorage.removeItem('loginFailureResponse');

  const { errorCode: code, errorMessage: message } = parseResponse(loginFailureResponse);

  batch(() => {
    dispatch(addError({ code, message }));
    dispatch(setSSOLoginFailure(true));
    dispatch(setSignOffDialogVisibility(false));
  });

  localStorage.removeItem('loginMethod');
  localStorage.removeItem('loginUrl');
  localStorage.removeItem('logOffMode');
  localStorage.removeItem('logoutUrl');
  localStorage.removeItem('taskSignOffUrl');
};

export const logoutRequest = (isClientLogout = false) => (dispatch, getState) => {
  const loginMethod = get(getState(), 'auth.loginMethod', localStorage.getItem('loginMethod'));
  const actorCodeWithDomain = get(
    getState(),
    'auth.actorCodeWithDomain',
    localStorage.getItem('actorCodeWithDomain')
  );
  const actorCode = localStorage.getItem('actorCode');

  localStorage.setItem('prevActorCodeWithDomain', actorCodeWithDomain);
  localStorage.setItem('prevActorCode', actorCode);

  localStorage.setItem('autoAuthentication', false);

  localStorage.removeItem('actorCode');
  localStorage.removeItem('actorCodeWithDomain');
  localStorage.removeItem('authErrors');
  localStorage.removeItem('isTokenUpdated');
  localStorage.removeItem('licId');
  localStorage.removeItem('loginMethod');
  localStorage.removeItem('sessionExpirationErrorCode');
  localStorage.removeItem('sessionExpirationErrorMessage');
  localStorage.removeItem('token');
  localStorage.removeItem('tokenLifeSpan');
  localStorage.removeItem('isUserActivityDetect');
  localStorage.removeItem('userFullName');
  localStorage.removeItem('username');
  localStorage.removeItem('culture');
  localStorage.removeItem('actorFirstName');
  localStorage.removeItem('actorLastName');
  localStorage.removeItem('loggedOnRoleCode');
  localStorage.removeItem('questionsAndAnswers');
  localStorage.removeItem('requireRegistration');
  localStorage.removeItem('isExternalUser');
  localStorage.removeItem('isArchiveMode');
  localStorage.removeItem('isArchiveUser');
  localStorage.removeItem('lastUserActivityTime');
  localStorage.removeItem('uiCacheObjects');
  localStorage.removeItem('entityDefinitionCacheDays');
  localStorage.removeItem('detailFormCacheDays');
  localStorage.removeItem('userCacheKey');
  localStorage.removeItem('smartInsightApplicationURL');
  localStorage.removeItem('maxAllowedContentLength');

  if (isClientLogout) {
    window.logout(false);
  } else {
    const apiData = syncAPICall('logout');
    if (apiData.hasError) {
      dispatch(handleErrors(apiData.errors));
    }
  }

  dispatch(logout());

  if (isEqual(LOGIN_METHODS.SAML, loginMethod)) {
    const logOffMode = localStorage.getItem('logOffMode');
    const logoutUrl = localStorage.getItem('logoutUrl');

    localStorage.removeItem('loginUrl');
    localStorage.removeItem('logOffMode');
    localStorage.removeItem('logoutUrl');
    localStorage.removeItem('taskSignOffUrl');

    if (isEqual(logOffMode, 'AppAndIdP') && logoutUrl) {
      window.location.href = logoutUrl;
    }
  }

  return Urls.LOGIN;
};

export const finishSignOff = () => (dispatch, getState) => {
  const payload = { showSignOffDialog: false, showFEDSSOPopup: false, isSignOffStep: false };
  const signOffResponse = localStorage.getItem('signOffResponse');

  localStorage.removeItem('signOffResponse');

  if (isNil(signOffResponse)) {
    dispatch(setTokenStatus({ isTokenUpdated: true, ...payload }));

    return;
  }

  const response = parseResponse(signOffResponse);

  if (response.hasError) {
    const { errorCode, errorMessage } = response;
    const sessionExpirationErrorCode = selectSessionExpirationErrorCode(getState());
    const shouldLogout = errorCode === '50001';
    const hasExpired = errorCode === sessionExpirationErrorCode;
    const isServerException = !(hasExpired || shouldLogout);

    batch(() => {
      dispatch(setTokenStatus({ isTokenUpdated: false, ...payload }));

      if (isServerException) {
        dispatch(
          addError({
            code: window.decodeURIComponent(errorCode),
            message: window.decodeURIComponent(errorMessage),
          })
        );
      }

      if (shouldLogout) {
        dispatch(expireToken(errorMessage, errorCode));
        dispatch(logoutRequest(true));
      }
    });
  } else {
    dispatch(setTokenStatus({ isTokenUpdated: true, ...payload }));
  }
};

export const changePassword = (bodyData) => async (dispatch) => {
  try {
    dispatch({ type: PASSWORD_CHANGE_REQUEST });

    const response = await smartSolveApi.post('/password/change', bodyData);
    const data = get(response, 'data');
    const hasError = get(data, 'hasError');

    if (hasError) {
      const { errorMessage: message, errorNumber: status } = get(data, 'errors.0');

      dispatch(passwordChangeFailure({ message, status }));
    } else {
      const message = get(data, 'Message', '');

      batch(() => {
        dispatch(addSuccess({ message }));
        dispatch(passwordChangeSuccess());
        dispatch(clearErrors());
      });
    }
  } catch (error) {
    const passwordChangeError = error.response
      ? {
          status: error.response.status,
          message: error.response.data.message,
        }
      : { message: 'Network error' };

    dispatch(passwordChangeFailure(passwordChangeError));
  }
};

export const login = (credentials) => (dispatch, getState) => {
  const prevCulture = localStorage.getItem('culture');

  dispatch({ type: LOGIN_REQUEST });

  return smartSolveApi
    .post('/login', {
      Username: credentials.username,
      Password: credentials.password,
      Language: prevCulture,
    })
    .then(
      async (response) => {
        const data = get(response, 'data');
        const hasError = get(data, 'hasError');

        if (!response) {
          dispatch(loginFailure({ message: 'Network error' }));
          return;
        }

        if (hasError || response.status !== 200) {
          const { errorMessage: message, errorNumber: status } = get(data, 'errors.0');
          dispatch(loginFailure({ message, status }));
        } else {
          const pickDataProp = partial(get, data);
          const actorCode = pickDataProp('ActorCode');
          const actorCodeWithDomain = window.decodeURIComponent(
            pickDataProp('ActorCodeWithDomain', '')
          );
          const actorFirstName = pickDataProp('ActorFirstName');
          const actorLastName = pickDataProp('ActorLastName');
          const licId = pickDataProp('licId');
          const sessionExpirationErrorCode = pickDataProp('sessionExpirationErrorCode');
          const sessionExpirationErrorMessage = pickDataProp('sessionExpirationErrorMessage');
          const token = pickDataProp('Token');
          const tokenLifeSpan = pickDataProp('TokenLifeSpan');
          const username = pickDataProp('UserFullName');
          const requirePasswordChange = pickDataProp('RequirePasswordChange');
          const requireRegistration = pickDataProp('ExternalUserNeedsRegistration');
          const isExternalUser = pickDataProp('IsExternalUser');
          const isPasswordChangeEnabled = parseBoolean(requirePasswordChange);
          const loginMethod = get(getState(), 'auth.loginMethod');
          const showPasswordChangeScreen =
            isEqual(LOGIN_METHODS.LDAP, loginMethod) && isPasswordChangeEnabled;
          const culture = pickDataProp('culture');
          const loggedOnRoleCode = pickDataProp('LoggedOnRoleCode');
          const isArchiveMode = parseBoolean(localStorage.getItem('isArchiveMode'));
          const isArchiveUser = pickDataProp('IsArchiveUser');
          const smartInsightApplicationURL = pickDataProp('SmartInsightApplicationURL');
          const maxAllowedContentLength = pickDataProp('MaxAllowedContentLength');

          const authData = {
            actorCode,
            actorCodeWithDomain,
            actorFirstName,
            actorLastName,
            isExternalUser,
            licId,
            sessionExpirationErrorCode,
            sessionExpirationErrorMessage,
            token,
            tokenLifeSpan,
            username,
            userLogonCode: credentials.username,
            culture,
            loggedOnRoleCode,
            requireRegistration,
            isArchiveUser,
            isArchiveMode,
            smartInsightApplicationURL,
            maxAllowedContentLength,
          };
          const prevActorCodeWithDomain = localStorage.getItem('prevActorCodeWithDomain');
          const hasLogonUserChanged =
            hasValue(prevActorCodeWithDomain) && actorCodeWithDomain !== prevActorCodeWithDomain;

          const prevQuestionsAndAnswers = localStorage.getItem('questionsAndAnswers');
          const questionsAndAnswers = hasValue(prevQuestionsAndAnswers)
            ? prevQuestionsAndAnswers
            : '';
          const hasCultureChanged = hasValue(prevCulture) && culture !== prevCulture;

          const adminAppUrl = getEnvVar('REACT_APP_ADMIN_APP_URL');
          localStorage.setItem('adminAppUrl', adminAppUrl);

          const clearWarningsFilter = ({ source }) => source !== WARNING_SOURCES.SERVER;
          const clearErrorsFilter = ({ code, isTopBanner }) =>
            code !== sessionExpirationErrorCode && code !== '50001' && !isTopBanner;

          if (showPasswordChangeScreen) {
            dispatch(setRequirePasswordChange(true, credentials.username));
            dispatch(clearWarnings(clearWarningsFilter));
            dispatch(clearErrors(clearErrorsFilter));
            return;
          }

          localStorage.removeItem('tenant');
          localStorage.removeItem('prevActorCodeWithDomain');
          localStorage.removeItem('prevActorCode');
          localStorage.setItem('actorCode', actorCode);
          localStorage.setItem('actorCodeWithDomain', actorCodeWithDomain);
          localStorage.setItem('actorFirstName', actorFirstName);
          localStorage.setItem('actorLastName', actorLastName);
          localStorage.setItem('licId', licId);
          localStorage.setItem('loginMethod', loginMethod);
          localStorage.setItem('sessionExpirationErrorCode', sessionExpirationErrorCode);
          localStorage.setItem('sessionExpirationErrorMessage', sessionExpirationErrorMessage);
          localStorage.setItem('token', token);
          localStorage.setItem('tokenLifeSpan', tokenLifeSpan);
          localStorage.setItem('isUserActivityDetect', false);
          localStorage.setItem('userFullName', username);
          culture && localStorage.setItem('culture', culture);
          localStorage.setItem('questionsAndAnswers', questionsAndAnswers);
          localStorage.setItem('requireRegistration', requireRegistration);
          localStorage.setItem('isExternalUser', isExternalUser);
          localStorage.setItem('isArchiveUser', isArchiveUser);
          localStorage.setItem('smartInsightApplicationURL', smartInsightApplicationURL);
          localStorage.setItem('maxAllowedContentLength', maxAllowedContentLength);
          setUserCacheKey(actorCodeWithDomain);

          if (hasCultureChanged) {
            dispatch(updateLogonUser(hasLogonUserChanged));
            window.location.reload();
          }

          await registerPasskeyCredentialsIfNeeded(authData, hasLogonUserChanged);

          if (token) {
            getUICacheObjects().then((response) => {
              if (response.hasError) {
                dispatch(handleErrors(response.errors));
                return;
              }

              handleGetUICacheObjectsResponse(response);
            });
          }

          batch(() => {
            dispatch(setRequirePasswordChange(false));
            dispatch(updateLogonUser(hasLogonUserChanged));
            dispatch(loginSuccess(authData, credentials.password));
            dispatch(clearWarnings(clearWarningsFilter));
            dispatch(clearErrors(clearErrorsFilter));
          });
        }
      },
      (error) =>
        !error.response
          ? dispatch(loginFailure({ message: 'Network error' }))
          : dispatch(
              loginFailure({
                status: error.response.status,
                message: error.response.data.message,
              })
            )
    );
};

export const fetchLoginMethod = (userName, isTenant = false) => async (dispatch) => {
  try {
    dispatch({ type: LOGIN_REQUEST });
    const response = await smartSolveApi.post('/getLoginMethod', {
      userName,
    });
    const data = get(response, 'data');
    const loginMethod = get(data, 'authMode', '');
    const hasError = get(data, 'hasError');

    if (hasError) {
      const { errorMessage: message, errorNumber: status } = get(data, 'errors.0');
      if (isTenant) {
        dispatch(loginFailure(null));
      } else {
        dispatch(loginFailure({ message, status }));
      }
    } else {
      const prevActorCodeWithDomain = localStorage.getItem('prevActorCodeWithDomain');
      const hasLogonUserChanged =
        hasValue(prevActorCodeWithDomain) &&
        lowerCase(userName) !== lowerCase(prevActorCodeWithDomain);

      if (hasLogonUserChanged) {
        closePrevChildrenWindows();
      }

      batch(() => {
        dispatch({ loginMethod, type: SET_LOGIN_METHOD });
        dispatch(loginFailure(null));
      });

      if (isEqual(LOGIN_METHODS.SAML, loginMethod)) {
        if (!localStorage.getItem('loginMethod')) {
          const logOffMode = get(data, 'logOffMode', '');
          const loginUrl = getSSOUrl(data, 'loginUrl', Urls.HOME);
          const logoutUrl = getSSOUrl(data, 'logoutUrl', Urls.LOGIN);
          const taskSignOffUrl = getSSOUrl(data, 'taskSignOffUrl', Urls.HOME);

          localStorage.setItem('loginMethod', loginMethod);
          localStorage.setItem('loginUrl', loginUrl);
          localStorage.setItem('logOffMode', logOffMode);
          localStorage.setItem('logoutUrl', logoutUrl);
          localStorage.setItem('taskSignOffUrl', taskSignOffUrl);
        }

        batch(() => {
          dispatch(showIdpView({}));
          dispatch(clearErrors());
        });
      }
    }
  } catch (error) {
    return !error.response
      ? dispatch(loginFailure({ message: 'Network error' }))
      : dispatch(
          loginFailure({
            status: error.response.status,
            message: error.response.data.message,
          })
        );
  }
};

const setSSOToken = (token) => (dispatch, getState) => {
  const showSignOffDialog = get(getState(), 'auth.showSignOffDialog');
  const actorCode = getQsParam('ActorCode', localStorage.getItem('actorCode'));
  const actorFirstName = getQsParam('ActorFirstName', localStorage.getItem('actorFirstName'));
  const actorLastName = getQsParam('ActorLastName', localStorage.getItem('actorLastName'));
  const actorCodeWithDomain = window.decodeURIComponent(
    getQsParam('ActorCodeWithDomain', localStorage.getItem('actorCodeWithDomain'))
  );
  const licId = getQsParam('licId', localStorage.getItem('licId'));
  const username = getQsParam('UserFullName', localStorage.getItem('userFullName'));
  const sessionExpirationErrorCode = getQsParam(
    'sessionExpirationErrorCode',
    localStorage.getItem('sessionExpirationErrorCode')
  );
  const sessionExpirationErrorMessage = getQsParam(
    'sessionExpirationErrorMessage',
    localStorage.getItem('sessionExpirationErrorMessage')
  );
  const culture = getQsParam('culture', localStorage.getItem('culture'));
  const loggedOnRoleCode = getQsParam('LoggedOnRoleCode', localStorage.getItem('loggedOnRoleCode'));
  const loginMethod = LOGIN_METHODS.SAML;
  const prevActorCodeWithDomain = localStorage.getItem('prevActorCodeWithDomain');
  const hasLogonUserChanged =
    hasValue(prevActorCodeWithDomain) && actorCodeWithDomain !== prevActorCodeWithDomain;
  const requireRegistration = parseBoolean(
    getQsParam('ExternalUserNeedsRegistration', localStorage.getItem('requireRegistration'))
  );
  const isExternalUser = parseBoolean(
    getQsParam('IsExternalUser', localStorage.getItem('isExternalUser'))
  );
  const isArchiveMode = parseBoolean(localStorage.getItem('isArchiveMode'));
  const isArchiveUser = parseBoolean(
    getQsParam('IsArchiveUser', localStorage.getItem('isArchiveUser'))
  );
  const smartInsightApplicationURL = getQsParam(
    'SmartInsightApplicationURL',
    localStorage.getItem('smartInsightApplicationURL')
  );
  const maxAllowedContentLength = getQsParam(
    'MaxAllowedContentLength',
    localStorage.getItem('maxAllowedContentLength')
  );
  const tokenLifeSpan = getQsParam('TokenLifeSpan', localStorage.getItem('tokenLifeSpan'));

  const authData = {
    actorCode,
    actorCodeWithDomain,
    actorFirstName,
    actorLastName,
    licId,
    sessionExpirationErrorCode,
    sessionExpirationErrorMessage,
    token,
    username,
    culture,
    loggedOnRoleCode,
    requireRegistration,
    isExternalUser,
    isArchiveUser,
    isArchiveMode,
    tokenLifeSpan,
    smartInsightApplicationURL,
    maxAllowedContentLength,
  };

  localStorage.removeItem('prevActorCodeWithDomain');
  localStorage.removeItem('prevActorCode');

  batch(() => {
    dispatch({ loginMethod, type: SET_LOGIN_METHOD });
    dispatch(updateLogonUser(hasLogonUserChanged));
    dispatch(loginSuccess(authData));

    if (showSignOffDialog) {
      dispatch(setSignOffDialogVisibility(false));
    }

    dispatch(clearErrors(filterNonExpiredErrors(sessionExpirationErrorCode)));
  });

  const adminAppUrl = getEnvVar('REACT_APP_ADMIN_APP_URL');
  localStorage.setItem('adminAppUrl', adminAppUrl);
  localStorage.setItem('actorCode', actorCode);
  localStorage.setItem('actorCodeWithDomain', actorCodeWithDomain);
  localStorage.setItem('licId', licId);
  localStorage.setItem('loginMethod', loginMethod);
  localStorage.setItem('sessionExpirationErrorCode', sessionExpirationErrorCode);
  localStorage.setItem('sessionExpirationErrorMessage', sessionExpirationErrorMessage);
  localStorage.setItem('token', token);
  localStorage.setItem('isUserActivityDetect', false);
  localStorage.setItem('userFullName', username);
  localStorage.setItem('actorFirstName', actorFirstName);
  localStorage.setItem('actorLastName', actorLastName);
  culture && localStorage.setItem('culture', culture);
  localStorage.setItem('loggedOnRoleCode', loggedOnRoleCode);
};

const loginAsWINNTUser = () => (dispatch) => {
  const token = parseNilOrValue(localStorage.getItem('token'));

  if (!token) {
    return null;
  }

  const actorCode = localStorage.getItem('actorCode');
  const actorCodeWithDomain = localStorage.getItem('actorCodeWithDomain');
  const actorFirstName = localStorage.getItem('actorFirstName');
  const actorLastName = localStorage.getItem('actorLastName');
  const licId = localStorage.getItem('licId');
  const loginMethod = localStorage.getItem('loginMethod');
  const username = localStorage.getItem('userFullName');
  const sessionExpirationErrorCode = localStorage.getItem('sessionExpirationErrorCode');
  const sessionExpirationErrorMessage = localStorage.getItem('sessionExpirationErrorMessage');
  const culture = localStorage.getItem('culture');
  const loggedOnRoleCode = localStorage.getItem('loggedOnRoleCode');
  const tokenLifeSpan = localStorage.getItem('tokenLifeSpan');
  const requireRegistration = parseBoolean(localStorage.getItem('requireRegistration'));
  const isExternalUser = parseBoolean(localStorage.getItem('isExternalUser'));
  const isArchiveMode = parseBoolean(localStorage.getItem('isArchiveMode'));
  const isArchiveUser = parseBoolean(localStorage.getItem('isArchiveUser'));
  const smartInsightApplicationURL = localStorage.getItem('smartInsightApplicationURL');
  const maxAllowedContentLength = localStorage.getItem('maxAllowedContentLength');

  return dispatch(
    loginSuccess({
      actorCode,
      actorCodeWithDomain,
      culture,
      actorFirstName,
      actorLastName,
      isExternalUser,
      licId,
      loginMethod,
      sessionExpirationErrorCode,
      sessionExpirationErrorMessage,
      requireRegistration,
      token,
      username,
      loggedOnRoleCode,
      isArchiveMode,
      isArchiveUser,
      tokenLifeSpan,
      smartInsightApplicationURL,
      maxAllowedContentLength,
    })
  );
};

const loginAsSSOUser = () => (dispatch) => {
  const token = getQsParam('Token', localStorage.getItem('token'));

  return token ? dispatch(setSSOToken(token)) : null;
};

export const verifyAccess = (token) => (dispatch, getState) => {
  const loginMethod = get(getState(), 'auth.loginMethod') || localStorage.getItem('loginMethod');
  const isSSOLoginMethod = isEqual(LOGIN_METHODS.SAML, loginMethod);
  const { isOnline } = getState().networkStatus;

  const action = isSSOLoginMethod || token ? loginAsSSOUser : loginAsWINNTUser;

  if (isOnline) {
    return dispatch(action());
  }

  return dispatch(checkPasskeyCredentialsIfNeeded(isOnline));
};

export const getLocales = () => async (dispatch) => {
  try {
    const { AvailableLocales, hasError, errors } = await fetchLocales();

    if (hasError) {
      throw errors;
    }

    dispatch(setLocales({ locales: AvailableLocales }));
  } catch (error) {
    dispatch(handleErrors(error, { isTopBanner: true }));
  }
};

export const getTimezones = () => async (dispatch) => {
  try {
    const { AvailableTimeZones, hasError, errors } = await fetchTimezones();

    if (hasError) {
      throw errors;
    }

    dispatch(setTimezones({ timezones: AvailableTimeZones }));
  } catch (error) {
    dispatch(handleErrors(error, { isTopBanner: true }));
  }
};

export const registration = (params) => (dispatch) => {
  dispatch({ type: REGISTRATION_REQUEST });

  return smartSolveApi.post('/external/register', params).then(
    (response) => {
      const data = get(response, 'data');
      const hasError = get(data, 'hasError');
      const requireRegistrationApproval = get(data, 'ExternalUserNeedsApproval');

      if (hasError) {
        const { errorMessage: message, errorNumber: status } = get(data, 'errors.0');
        dispatch(registrationFailure({ message, status }));

        return;
      }

      localStorage.setItem('requireRegistration', false);

      if (requireRegistrationApproval) {
        dispatch(logoutRequest());
      } else {
        dispatch(registrationSuccess({ requireRegistrationApproval }));
      }
    },
    (error) => {
      !error.response
        ? dispatch(registrationFailure({ message: 'Network error' }))
        : dispatch(
            registrationFailure({
              status: error.response.status,
              message: error.response.data.message,
            })
          );
    }
  );
};
